import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DrawerItems,
  DrawerItemsTitle,
} from '@/components/Drawer/Drawer.styles';
import { Drawer } from '@/components/Drawer/Drawer';
import { AngleBackIcon } from '@/components/UI/Icons/Icons';
import { Property, PropertyOption } from '@/api/types/categories.types';
import { Collection } from '@/components/FiltersDrawer/FiltersDrawer.types';
import { CollectionSelectOption } from '@/components/FiltersDrawer/CollectionSelectOption';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';
import { CheckboxAppearance } from '@/components/UI/Checkbox/Checkbox.types';

type Props = {
  isOpen: boolean;
  toggle: (v?: boolean) => void;
  propertyLegacyId?: string;
  options?: PropertyOption[];
  collection?: Collection;
  setCollection?: React.Dispatch<React.SetStateAction<Collection>>;
  allProperties?: Property[];
  popularOptions?: PropertyOption[];
  onItemClick?: (option: PropertyOption) => void;
  checkboxAppearance?: CheckboxAppearance;
};

export const CollectionSelectDrawer: FC<Props> = ({
  isOpen,
  toggle,
  propertyLegacyId,
  options,
  collection,
  allProperties,
  popularOptions,
  onItemClick,
  checkboxAppearance,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const name = allProperties?.find(
    el => el.legacyResourceId === propertyLegacyId
  )?.name;

  function onOptionClick(value: PropertyOption) {
    if (onItemClick) {
      onItemClick(value);
    }
  }

  const list = options?.filter(option =>
    option.name.toLowerCase().includes(query.toLowerCase())
  );

  const withPopularOptions = !!popularOptions?.length && !query;

  return (
    <Drawer
      isOpen={isOpen}
      title={name}
      left={{
        icon: <AngleBackIcon />,
        onClick: () => toggle(false),
      }}
      onSearch={setQuery}
      dataTestId={SearchResultsPageTestIds.collectionSelectDrawer}
    >
      {withPopularOptions && (
        <>
          <DrawerItemsTitle>{t('common.popular')}</DrawerItemsTitle>
          <DrawerItems>
            {popularOptions.map(option => {
              return (
                <CollectionSelectOption
                  key={option.name}
                  option={option}
                  collection={collection}
                  onOptionClick={onOptionClick}
                  appearance={checkboxAppearance}
                />
              );
            })}
          </DrawerItems>

          <DrawerItemsTitle $hasTopGutter>{t('common.all')}</DrawerItemsTitle>
        </>
      )}

      <DrawerItems>
        {list?.map(option => {
          return (
            <CollectionSelectOption
              key={option.name}
              option={option}
              collection={collection}
              onOptionClick={onOptionClick}
              appearance={checkboxAppearance}
            />
          );
        })}
      </DrawerItems>
    </Drawer>
  );
};

CollectionSelectDrawer.displayName = `CollectionSelectDrawer`;
